import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { pairwise, startWith, takeUntil } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { InteractionMode, SocialState, UpdateType } from 'messages/websocket.messages';

import SocialStateHelper from '../social-state-machine/social-state-helper';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../state/app';
import { actions, mediaSelectors } from '../state/media';

import { SceneService } from './scene.service';
import { VideoRoomService } from './video-room.service';

/**
 * MultimediaUserService owns the business logic for handling users actions to manage audio/video.
 * Also, it's responsible for changing the video/audio availability, to have a centralized
 * place to do this and remove this logic's handling from JoinSocialHomeComponent.
 *
 * This is paired with MultimediaRoomService, which manages audio/video changes as a result of updates to the room, particularly
 * resulting from changes in the room and its social state.
 *
 * The difference between Multimedia Service and SoundControl is that sound control is responsible for setting
 * sound level based on distance/events, multimedia service is responsible only for audio/video
 * input toggling from the user's end.
 */
@Injectable({
  providedIn: 'root',
})
export class MultimediaUserService extends Loggable {
  private isPresenter = false;
  private audienceCanUnmute = true;
  private user?: User;
  private isCrossRoom = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isQueuedToSpeak = false;

  // Handle Multimedia presentation controls
  public isSayingSomething$ = new BehaviorSubject<boolean>(false);
  private get isSayingSomething(): boolean {
    return this.isSayingSomething$.value;
  }
  private set isSayingSomething(sayingSomething: boolean) {
    if (sayingSomething !== this.isSayingSomething) {
      this.isSayingSomething$.next(sayingSomething);
    }
  }
  public isHandRaised$ = new BehaviorSubject<boolean>(false);
  public canPushToTalk$ = new BehaviorSubject<boolean>(false);
  public canRaiseHand$ = new BehaviorSubject<boolean>(false);
  public interactionMode?: InteractionMode;

  private userHasMuted = false;
  private isTemporaryMicEnabled = false;
  private isMicEnabled = true;
  public isVideoEnabled = true;

  public speakingList: string[] = [];

  public constructor(
    private videoRoomService: VideoRoomService,
    private sceneService: SceneService,
    private store: Store,
  ) {
    super();
    this.setDisplayName('MultimediaUserService');
    window.addEventListener('keypress', this.enableMicListener, false);
    window.addEventListener('keyup', this.disableMicListener, false);
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject(1);

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isMicEnabled = enabled));

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isVideoEnabled = enabled));

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => (this.user = user));

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => (this.isCrossRoom = isCrossRoom));

    this.store
      .select(socialAppSelectors.isQueuedToSpeak)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isQueuedToSpeak) => (this.isQueuedToSpeak = isQueuedToSpeak));

    SocialStateMachine.shared.stateChanged$
      .pipe(startWith({} as SocialState), pairwise(), takeUntil(this.destroyed$))
      // eslint-disable-next-line
      .subscribe(async ([previousState, state]) => {
        if (!state) {
          return;
        }

        const { userId } = this.user ?? {};
        if (!userId) {
          return;
        }

        // Reminder, getEventInfo() provides a helpful default for settings when no event is in progress.
        const eventInfo = SocialStateHelper.getEventInfo(state);
        if (isEqual(state.socialEvent, previousState.socialEvent)) {
          return;
        }

        const presenterCanHearAudience = eventInfo.presenterCanHearAudience;
        const allowGroupConversations = eventInfo.allowGroupConversations;

        this.audienceCanUnmute = allowGroupConversations || presenterCanHearAudience;

        const userIsPresenter = eventInfo.presenterIds.includes(userId);
        this.isPresenter = userIsPresenter;

        const isSayingSomething = SocialStateHelper.isSpeakingParticipant({ state, userId });
        this.isSayingSomething = isSayingSomething;

        const canPushToTalk = SocialStateHelper.pushToTalkAvailable({ state, userId });
        if (canPushToTalk !== this.canPushToTalk$.value) {
          this.canPushToTalk$.next(canPushToTalk);
        }

        this.interactionMode = SocialStateHelper.getInteractionMode(state);

        const canRaiseHand = SocialStateHelper.isQueuedQAndAHappening(state);
        if (canRaiseHand !== this.canRaiseHand$.value) {
          this.canRaiseHand$.next(canRaiseHand);
        }

        if (this.isQueuedToSpeak !== this.isHandRaised$.value) {
          this.isHandRaised$.next(this.isQueuedToSpeak);
        }

        /* If interaction mode is push to speak and a speaker's mic isn't enabled, we enable their mic temporarily, since they pushed to talk.
         * We don't update this.userHasMuted since that value will be respected once they are no longer speaking to room . */
        const presentationIsPushToTalk = SocialStateHelper.isPushToTalkPresentationHappening(state);
        if (isSayingSomething && presentationIsPushToTalk) {
          return this.enableMic();
        }

        /* The state has no impact on audio enabling if there is no presentation, group conversations are
         * enabled, or the user is a presenter. Only the user mute status matters in this case. */
        if (!eventInfo.isPresentation || eventInfo.allowGroupConversations || userIsPresenter) {
          const micEnabled = !this.userHasMuted;
          if (this.isMicEnabled !== micEnabled) {
            if (micEnabled) {
              this.enableMic();
            } else {
              this.disableMic();
            }
          }
          return;
        }

        /* Speaking logic for participants during a presentation where group conversations are disabled */
        if (!isSayingSomething) {
          return this.disableMic();
        }

        // Otherwise, enable mic for speaking users in Q&A if the user hasn't muted themselves.
        if (isSayingSomething && !this.userHasMuted) {
          return this.enableMic();
        }
      });
  }

  public reinitMediaState(): void {
    this.store.dispatch(actions.UNMUTE_AUDIO());
    this.store.dispatch(actions.UNMUTE_VIDEO());
  }

  private enableMicListener = (e: KeyboardEvent): void => {
    if (
      e.code === 'Space' &&
      !this.isMicEnabled &&
      !this.isTemporaryMicEnabled &&
      this.userCanUnmute &&
      document.activeElement?.id !== 'txtAreaChatComposer'
    ) {
      this.userUnmute();
      this.isTemporaryMicEnabled = true;
    }
  };

  private disableMicListener = (e: KeyboardEvent): void => {
    if (e.code === 'Space' && this.isMicEnabled && this.userCanUnmute && this.isTemporaryMicEnabled) {
      this.userMute();
      this.isTemporaryMicEnabled = false;
    }
  };

  public disableVideo(): void {
    if (!this.isVideoEnabled) {
      return;
    }
    this.store.dispatch(actions.MUTE_VIDEO());
    // const customFields: ChurnZeroCustomFields = { Camera: 'Off' };
    // this.churnZeroService.trackEvent(ChurnZeroEvent.CAMERA, customFields);
  }

  public enableVideo(): void {
    if (this.isVideoEnabled) {
      return;
    }
    this.store.dispatch(actions.UNMUTE_VIDEO());
    // const customFields: ChurnZeroCustomFields = { Camera: 'On' };
    // this.churnZeroService.trackEvent(ChurnZeroEvent.CAMERA, customFields);
  }

  public toggleVideo(): void {
    this.isVideoEnabled = !this.isVideoEnabled;
    this.store.dispatch(this.isVideoEnabled ? actions.UNMUTE_VIDEO() : actions.MUTE_VIDEO());
    // Log Churn Zero Camera Event
    // const customFields: ChurnZeroCustomFields = { Camera: this.isVideoEnabled ? 'On' : 'Off' };
    // this.churnZeroService.trackEvent(ChurnZeroEvent.CAMERA, customFields);
  }

  private get userCanUnmute(): boolean {
    // User can mute/unmute during Q&A but not Push To Speak settings, since the user expects to be unmuted
    // when they press "Speak to room", but may not be prepared to be unmuted once called on.
    return this.audienceCanUnmute || this.isPresenter || (this.isSayingSomething && this.canRaiseHand$.value);
  }

  public enableMic(): void {
    // Mic can be enabled temporarily while user has muted, so we don't prevent mic from being enabled in this case.
    if (this.isMicEnabled) {
      return;
    }
    this.store.dispatch(actions.UNMUTE_AUDIO());
    // Notify track to send OutgoingMessageType
    const notify = true;
    this.videoRoomService.activeRoom?.localParticipant.enableAudio(notify);

    // Log ChurnZero Mic Event
    // const customFields: ChurnZeroCustomFields = { Microphone: 'On' };
    // this.churnZeroService.trackEvent(ChurnZeroEvent.MIC, customFields);
  }

  public disableMic(): void {
    if (!this.isMicEnabled) {
      return;
    }
    this.store.dispatch(actions.MUTE_AUDIO());
    // Notify track to send OutgoingMessageType
    const notify = true;
    this.videoRoomService.activeRoom?.localParticipant.disableAudio(notify);

    // Log ChurnZero Mic Event
    // const customFields: ChurnZeroCustomFields = { Microphone: 'Off' };
    // this.churnZeroService.trackEvent(ChurnZeroEvent.MIC, customFields);
  }

  private userMute(): void {
    this.userHasMuted = true;
    this.disableMic();
  }

  private userUnmute(): void {
    this.userHasMuted = false;
    this.enableMic();
  }

  public userToggleAudio(): void {
    if (!this.userCanUnmute) {
      if (!this.userHasMuted) {
        this.userMute();
      }
      return;
    }
    if (this.userHasMuted) {
      this.userUnmute();
    } else {
      this.userMute();
    }
  }

  public setParticipantVideo(userId: string, enabled: boolean): void {
    const participant = this.sceneService.sceneManager?.getParticipant(userId);
    if (participant) {
      participant.enableRemoteVideo(enabled);
    }
  }

  public setParticipantAudio(userId: string, enabled: boolean): void {
    const participant = this.sceneService.sceneManager?.getParticipant(userId);
    if (participant) {
      participant.enableMic(enabled);
    }
  }

  public async handleRequestPushToTalk(): Promise<void> {
    const { firstName, lastName, userId } = this.user ?? {};

    if (!userId) {
      return;
    }

    const slots = this.interactionMode === InteractionMode.SINGLE_PUSH_TO_SPEAK ? 1 : undefined;

    await SocialStateMachine.shared
      .requestAddParticipantToSpeaking({
        participant: { userId, firstName: firstName ?? '', lastName: lastName ?? '' },
        userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
        slots,
      })
      .then(() => {
        if (this.interactionMode === InteractionMode.SINGLE_PUSH_TO_SPEAK) {
          this.speakingList[0] = userId;
          return;
        }
        this.speakingList.push(userId);
        return;
      });
  }

  public async handleRequestEndPushToTalk(otherUserId?: string): Promise<void> {
    const { userId } = this.user ?? {};

    if (!userId) {
      return;
    }

    const userIdToEnd = otherUserId ?? userId;
    await SocialStateMachine.shared
      .requestRemoveSpeakingParticipant({
        participantId: userIdToEnd,
        userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .then(() => {
        const index = this.speakingList.indexOf(userId);
        if (index !== -1) {
          this.speakingList.splice(index, 1);
        }
      });
  }

  public async handleRequestRaiseHand(): Promise<void> {
    const { firstName, lastName, userId } = this.user ?? {};
    const { state } = SocialStateMachine.shared;

    if (!userId || !state) {
      return;
    }

    if (this.isQueuedToSpeak) {
      return;
    }

    await SocialStateMachine.shared
      .requestAddParticipantToSpeakingQueue({
        participant: { userId, firstName: firstName ?? '', lastName: lastName ?? '' },
        userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request add speaking participant', e));
  }

  public async handleRequestLowerHand(otherUserId?: string): Promise<void> {
    if (!this.user) {
      return;
    }
    const { firstName, lastName, userId } = this.user;
    const { state } = SocialStateMachine.shared;

    if (!userId || !state) {
      return;
    }

    const userIdToEnd = otherUserId ? otherUserId : userId;
    if (!SocialStateHelper.isQueuedToSpeak({ state, userId: userIdToEnd })) {
      return;
    }
    await SocialStateMachine.shared
      .requestRemoveParticipantFromSpeakingQueue({
        participant: { userId, firstName: firstName ?? '', lastName: lastName ?? '' },
        userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      })
      .catch((e) => this.error('Error request remove speaking from queue', e));
  }

  public destroy(): void {
    // This is best effort, so we don't need to wait for it to complete. The user should leave anyway.
    void this.handleRequestLowerHand();
    window.removeEventListener('keypress', this.enableMicListener, false);
    window.removeEventListener('keyup', this.disableMicListener, false);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
